import React from 'react';

interface Props {
    children: React.ReactNode;
}

interface State {
    hasError: boolean;
}

const RELOAD_GUARD_KEY = 'sg_chunk_reload_guard';

/** Détecte l'échec de chargement d'un chunk React.lazy() — survient quand une requête
 *  de chargement de page est interrompue (ex: clics rapides entre onglets qui annulent la
 *  requête réseau en cours). Observé plus fréquemment sur Edge que Chrome, dont le réseau
 *  annule plus agressivement les fetch en cours lors d'une navigation. */
const estErreurChargementChunk = (error: Error): boolean =>
    /loading chunk|failed to fetch dynamically imported module|error loading dynamically imported module/i
        .test(error.message ?? '');

class ErrorBoundary extends React.Component<Props, State> {
    state: State = { hasError: false };

    static getDerivedStateFromError(): State {
        return { hasError: true };
    }

    componentDidCatch(error: Error) {
        if (estErreurChargementChunk(error)) {
            // Un seul rechargement automatique — le sessionStorage évite une boucle infinie
            // si le rechargement lui-même retombe sur la même erreur (ex: backend down).
            if (!sessionStorage.getItem(RELOAD_GUARD_KEY)) {
                sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
                window.location.reload();
                return;
            }
        }
        console.error('Erreur non interceptée:', error);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '60vh',
                    gap: '16px',
                    padding: '24px',
                    textAlign: 'center',
                }}>
                    <i className="bi bi-exclamation-triangle" style={{ fontSize: '40px', color: '#f59e0b' }} />
                    <div style={{ fontSize: '16px', fontWeight: 600, color: '#374151' }}>
                        Une erreur inattendue s'est produite
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280', maxWidth: '420px' }}>
                        Rechargez la page pour continuer. Si le problème persiste, contactez le support.
                    </div>
                    <button
                        onClick={() => {
                            sessionStorage.removeItem(RELOAD_GUARD_KEY);
                            window.location.reload();
                        }}
                        style={{
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            background: '#3b82f6',
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                    >
                        Recharger la page
                    </button>
                </div>
            );
        }
        return this.props.children;
    }
}

export default ErrorBoundary;
